import {isReadonly, potionInstance, potionURI} from './metadata';
import {QueryParams, RequestOptions} from './potion';
import {isJsObject} from './utils';


export interface ItemOptions {
    'readonly'?: string[];
}

export type ItemFetchOptions = Pick<RequestOptions, 'cache'>;
export type ItemQueryOptions = Pick<RequestOptions, 'cache' | 'paginate'>;

export interface ItemInitArgs {
    [key: string]: any;
}
/**
 * Base resource class for API resources.
 * Extending this class will make all resource operations available on the child classes.
 * NOTE: This is an abstract class and cannot be directly initiated.
 * 
 * \@example 
 * class User extends Item {}
 * 
 * User.fetch(1).then((user) => {
 *     user.update({name: 'John Doe'});
 * });
 * 
 * const fred = new User({name: 'Fred'});
 * fred.save();
 * 
 * const jane = User.fetch(1);
 * jane.then((jane) => {
 *     jane.alias = 'Joe';
 *     jane.save();
 * });
 * 
 * User.query().then((users) => {
 *     users[0].destroy();
 * });
 * @abstract
 */
export abstract class Item {
    [key: string]: any;
/**
 * Get a resource by id.
 * @template T
 * @param {?} id
 * @param {?=} __1
 * @return {?}
 */
static fetch<T extends Item>(id: number | string, {cache = true}: ItemFetchOptions = {}): Promise<T> {
        const /** @type {?} */ uri: string = potionURI(this);
        return potionInstance(this).fetch(`${uri}/${id}`, {
            method: 'GET',
            cache
        });
    }
/**
 * Query resources.
 * but it can also be used to further filter the results with {sort: any, where: any}.
 * @template T, R
 * @param {?=} queryParams
 * @param {?=} __1
 * @return {?}
 */
static query<T extends Item, R = T[]>(queryParams?: QueryParams | null, {paginate = false, cache = true}: ItemQueryOptions = {}): Promise<R> {
        const /** @type {?} */ uri: string = potionURI(this);
        return potionInstance(this).fetch(uri, {
            method: 'GET',
            params: queryParams,
            paginate,
            cache
        });
    }
/**
 * Get the first item.
 * but it can also be used to further filter the results with {sort: any, where: any}.
 * @template T
 * @param {?=} queryOptions
 * @return {?}
 */
static first<T extends Item>(queryOptions?: QueryParams): Promise<T> {
        return /** @type {?} */(( this.query(queryOptions)
            .then(items => items[0]) as Promise<T>));
    }
private $uri: string;
private $id: number | string | null = null;
/**
 * Create an instance of the class that extended the Item.
 * @param {?=} properties
 */
constructor(properties?: ItemInitArgs) {
        if (isJsObject(properties)) {
            Object.assign(this, properties);
        }
    }
/**
 * @return {?}
 */
get uri(): string {
        return this.$uri;
    }
/**
 * @return {?}
 */
get id(): number | string | null {
        return this.$id;
    }
/**
 * Compare current resource with another object.
 * @param {?} resource
 * @return {?}
 */
equals(resource: any): boolean {
        if (resource instanceof Item) {
            return this.id === resource.id && this.constructor.name === resource.constructor.name;
        }
        return false;
    }
/**
 * Get the JSON repr. of this item.
 * @return {?}
 */
toJSON(): any {
        const /** @type {?} */ properties: {[key: string]: any} = {};

        Object.keys(this)
            .filter(key => !key.startsWith('$') && !isReadonly(this.constructor, key))
            .forEach((key: string) => {
                properties[key] = this[key];
            });

        return properties;
    }
/**
 * Save the current item.
 * @return {?}
 */
save(): Promise<this> {
        if (this.uri || this.id) {
            return this.update(this.toJSON());
        }
        const /** @type {?} */ ctor = /** @type {?} */(( this.constructor as typeof Item));
        return potionInstance(ctor)
            .fetch(potionURI(ctor), {
                method: 'POST',
                body: this.toJSON(),
                cache: true
            });
    }
/**
 * Update the resource.
 * @param {?=} data
 * @return {?}
 */
update(data: any = {}): Promise<this> {
        return potionInstance( /** @type {?} */((this.constructor as typeof Item)))
            .fetch(this.uri, {
                cache: true,
                method: 'PATCH',
                body: data
            });
    }
/**
 * Destroy the current item.
 * @return {?}
 */
destroy(): Promise<void> {
        const /** @type {?} */ uri = this.uri;
        const /** @type {?} */ potion = potionInstance( /** @type {?} */((this.constructor as typeof Item)));
        const /** @type {?} */ cache = potion.cache;
        return potion.fetch(uri, {method: 'DELETE'})
            .then(clearCache);
/**
 * @return {?}
 */
function clearCache(): void {
            // Clear the item from cache if exists
            if (cache.get(uri)) {
                cache.remove(uri);
            }
        }
    }
}

function Item_tsickle_Closure_declarations() {
/** @type {?} */
Item.prototype.$uri;
/** @type {?} */
Item.prototype.$id;
}

